import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'stores/hooks';
import { doUpdateUserSettings } from 'actions/user';
import { useUserDetails } from 'selectors/user';
import { MapSettings } from 'types/map';
import { type AppState } from 'stores';

interface UseMapSettingsReturn {
  mapSettings: MapSettings | undefined;
  updateMapSettings: (mapSettings: MapSettings) => Promise<boolean>;
}

const useMapSettings = (): UseMapSettingsReturn => {
  const dispatch = useAppDispatch();
  const userSettings = useAppSelector((state: AppState) => state.backplane?.user?.settings);
  const userDetails = useUserDetails();

  // Get mapSettings from userSettings or localStorage fallback
  const mapSettings = useMemo(() => {
    if (userSettings?.mapSettings) {
      return userSettings.mapSettings;
    }

    // Fallback to localStorage if userSettings is undefined
    if (typeof localStorage !== 'undefined') {
      const storedSettings = localStorage.getItem('surftiler:mapSettings');
      if (storedSettings) {
        try {
          return JSON.parse(storedSettings);
        } catch (error) {
          console.error('Failed to parse stored map settings:', error);
        }
      }
    }

    return undefined;
  }, [userSettings?.mapSettings]);

  const updateMapSettings = useCallback(
    async (newMapSettings: MapSettings) => {
      try {
        // If user is logged in, dispatch to backend
        if (userDetails) {
          const result = await dispatch(doUpdateUserSettings({ mapSettings: newMapSettings }));
          return result.type === 'UPDATE_USER_SETTINGS/fulfilled';
        } else {
          // If user is not logged in, store in localStorage
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('surftiler:mapSettings', JSON.stringify(newMapSettings));
            return true;
          }
          return false;
        }
      } catch (error) {
        console.error('Failed to update map settings:', error);
        return false;
      }
    },
    [dispatch, userDetails],
  );

  return {
    mapSettings,
    updateMapSettings,
  };
};

export default useMapSettings;
